import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageSeo from "@/components/PageSeo";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";
import { logger } from "@/lib/logger";
import { useLanguage } from "@/i18n/LanguageContext";

const GROUP_HANDLES: { id: string; handles: string[] }[] = [
  {
    id: "advisory",
    handles: [
      "stage-1-investor-readiness-monthly-advisory",
      "stage-2-investor-engagement",
      "embedded-advisor",
    ],
  },
  {
    id: "book",
    handles: ["the-founder-laws"],
  },
  {
    id: "courses",
    handles: [
      "pitching-to-investors-how-to-tell-your-story-and-win-the-room",
      "pr-superpowers-building-credibility-in-a-noisy-world",
      "web3-the-future-of-money-understanding-the-next-internet",
      "scaling-up-strategies-for-fintech-companies",
      "go-to-market-strategy-how-startups-launch-learn-and-scale",
      "valuation-funding-101-how-investors-value-startups",
      "mastering-venture-capital-judgment-founder-edition",
    ],
  },
  {
    id: "speaking",
    handles: ["executive-and-keynote-speaking-engagement"],
  },
];

const Services = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts(40)
      .then(setProducts)
      .catch((e) => logger.error("Error loading products:", e))
      .finally(() => setLoading(false));
  }, []);

  const GROUPS = [
    {
      id: "advisory",
      title: t("services.groups.advisory.title"),
      blurb: t("services.groups.advisory.blurb"),
      handles: GROUP_HANDLES.find((g) => g.id === "advisory")!.handles,
    },
    {
      id: "book",
      title: t("services.groups.book.title"),
      blurb: t("services.groups.book.blurb"),
      handles: GROUP_HANDLES.find((g) => g.id === "book")!.handles,
    },
    {
      id: "courses",
      title: t("services.groups.courses.title"),
      blurb: t("services.groups.courses.blurb"),
      handles: GROUP_HANDLES.find((g) => g.id === "courses")!.handles,
    },
    {
      id: "speaking",
      title: t("services.groups.speaking.title"),
      blurb: t("services.groups.speaking.blurb"),
      handles: GROUP_HANDLES.find((g) => g.id === "speaking")!.handles,
    },
  ];

  const groupFor = (id: string) =>
    (GROUPS.find((g) => g.id === id)?.handles ?? [])
      .map((h) => products.find((p) => p.node.handle === h))
      .filter(Boolean) as ShopifyProduct[];

  // Catch-all: any product not explicitly assigned to a group still shows up
  // so new or renamed products never silently disappear from the page.
  const assigned = new Set(GROUP_HANDLES.flatMap((g) => g.handles));
  const otherProducts = products.filter((p) => !assigned.has(p.node.handle));

  return (
    <div className="min-h-screen bg-background">
      <PageSeo
        title={t("seo.services.title")}
        description={t("seo.services.description")}
        path="/services"
      />
      <Navigation />

      <header className="pt-32 pb-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-6">
            {t("services.header.title")}
          </h1>
          <p className="text-lg text-primary-foreground/80">
            {t("services.header.subtitle")}
          </p>
          <Button
            variant="accent"
            size="lg"
            className="mt-8 font-sans"
            onClick={() =>
              window.open("https://calendar.app.google/smFTnDe7izrtAPVg6", "_blank")
            }
          >
            {t("services.header.cta")}
          </Button>
        </div>
      </header>

      {/* Quick comparison */}
      <section className="py-16 border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
            {[
              {
                step: t("services.compare.stage1.step"),
                title: t("services.compare.stage1.title"),
                text: t("services.compare.stage1.text"),
                to: "/product/stage-1-investor-readiness-monthly-advisory",
              },
              {
                step: t("services.compare.stage2.step"),
                title: t("services.compare.stage2.title"),
                text: t("services.compare.stage2.text"),
                to: "/product/stage-2-investor-engagement",
              },
              {
                step: t("services.compare.ongoing.step"),
                title: t("services.compare.ongoing.title"),
                text: t("services.compare.ongoing.text"),
                to: "/product/embedded-advisor",
              },
            ].map((c) => (
              <Link
                key={c.step}
                to={c.to}
                className="group border rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all bg-card"
              >
                <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                  {c.step}
                </span>
                <h2 className="text-xl font-bold mt-2 mb-2">{c.title}</h2>
                <p className="text-muted-foreground text-sm mb-4">{c.text}</p>
                <span className="inline-flex items-center text-sm font-medium text-foreground">
                  {t("services.compare.viewProgramme")}
                  <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        GROUPS.map((group) => {
          const items = groupFor(group.id);
          if (items.length === 0) return null;
          return (
            <section key={group.id} className="py-16 border-b last:border-0">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mb-10">
                  <h2 className="text-3xl font-bold tracking-tight mb-3">{group.title}</h2>
                  <p className="text-muted-foreground">{group.blurb}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {items.map((p) => (
                    <ProductCard key={p.node.id} product={p} />
                  ))}
                </div>
              </div>
            </section>
          );
        })
      )}

      {!loading && otherProducts.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-3">{t("services.other.title")}</h2>
              <p className="text-muted-foreground">
                {t("services.other.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {otherProducts.map((p) => (
                <ProductCard key={p.node.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Services;
